import LAYOUT from './keno-layout.json';

/**
 * Keno drawn from its real-3D render (scripts/art/render/keno.html): the navy tray with a
 * dimple under each number, the draw rail along its back with the glass bowl at its left
 * end, and one ball of each colour, all seen by one orthographic camera. From that camera
 * every cell looks the same, so a ball rendered on the reference cell is the same picture
 * on any cell, moved by that cell's offset; a rail ball is that picture shrunk and moved
 * to its slot. The numbers are not in the pictures: the board writes them.
 */

type Box = [number, number, number, number];
type Point = [number, number];
const BOXES = LAYOUT.boxes as unknown as Record<string, Box>;
const CELLS = LAYOUT.scene.cells as unknown as Point[];
const REF: number = LAYOUT.scene.ref;
const RAIL = LAYOUT.scene.rail as unknown as { scale: number; slots: Point[]; mouth: Point };
const DIMPLE = LAYOUT.scene.dimple as unknown as [number, number];

/** Every piece the board draws: the rendered layers, plus two of light. */
export const KENO_PIECES = ['board', 'bowl', 'ball-cyan', 'ball-gold', 'ball-bead', 'ballshadow', 'sparkle', 'glow'] as const;
export type KenoPiece = (typeof KENO_PIECES)[number];

/**
 * What a number is this round. Open numbers are bare dimples; the rest hold a ball whose
 * look says which: picked (turquoise), picked and drawn (gold, the win), drawn but not
 * picked (a clear glass bead, there without competing with the picks).
 */
export type KenoMark = 'open' | 'picked' | 'hit' | 'drawn';

export function cellMark(picked: boolean, drawn: boolean): KenoMark {
  if (drawn) return picked ? 'hit' : 'drawn';
  return picked ? 'picked' : 'open';
}

/** The ball a marked number holds; an open number holds none. */
export const MARK_BALL: Record<Exclude<KenoMark, 'open'>, KenoPiece> = {
  picked: 'ball-cyan',
  hit: 'ball-gold',
  drawn: 'ball-bead',
};

export interface PctBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

function pct(box: Box, of: Box): PctBox {
  const [x, y, w, h] = box;
  const [ox, oy, ow, oh] = of;
  return { left: ((x - ox) / ow) * 100, top: ((y - oy) / oh) * 100, width: (w / ow) * 100, height: (h / oh) * 100 };
}

function union(a: Box, b: Box): Box {
  const x = Math.min(a[0], b[0]);
  const y = Math.min(a[1], b[1]);
  return [x, y, Math.max(a[0] + a[2], b[0] + b[2]) - x, Math.max(a[1] + a[3], b[1] + b[3]) - y];
}

/** The board's view: the tray and the bowl, which stands above the tray's back edge. */
export const VIEW: Box = union(BOXES.board!, BOXES.bowl!);
export const VIEW_ASPECT = VIEW[2] / VIEW[3];

/** Where a whole-frame layer (the tray, the bowl) sits in the view. */
export function layerBox(piece: 'board' | 'bowl'): PctBox {
  return pct(BOXES[piece]!, VIEW);
}

const BALL: Box = BOXES['ball-cyan']!;
const BALL_MID: Point = LAYOUT.scene.ballCentre as unknown as Point;

/** Where number `n` (1..40) has its ball and button on the board. */
export function cellBox(n: number): PctBox {
  const [x, y, w, h] = BALL;
  const [cx, cy] = CELLS[n - 1]!;
  const [rx, ry] = CELLS[REF]!;
  return pct([x + cx - rx, y + cy - ry, w, h], VIEW);
}

/** A piece's place inside a ball's box: the same on every cell, and on every rail slot. */
export function inCell(piece: string): PctBox {
  return pct(BOXES[piece]!, BALL);
}

/** The ball's middle inside its box, where its number is written. */
export function ballCentre(): { left: number; top: number } {
  const [x, y, w, h] = BALL;
  return { left: ((BALL_MID[0] - x) / w) * 100, top: ((BALL_MID[1] - y) / h) * 100 };
}

/** The dimple's rim inside a ball's box: where a picked number's ring glows. */
export function dimpleInCell(): PctBox {
  const [w, h] = DIMPLE;
  return pct([BALL_MID[0] - w / 2, BALL_MID[1] - h / 2, w, h], BALL);
}

export const RAIL_SLOTS = RAIL.slots.length;

/** Where the `i`-th drawn ball (0-based, draw order) rests on the rail. */
export function railBox(i: number): PctBox {
  const [x, y, w, h] = BALL;
  const s = RAIL.scale;
  const [sx, sy] = RAIL.slots[i]!;
  return pct([sx - (BALL_MID[0] - x) * s, sy - (BALL_MID[1] - y) * s, w * s, h * s], VIEW);
}

/**
 * How far the bowl's mouth is from slot `i`, in percent of a rail ball's own box: the
 * ball starts there and rolls home, so a translate of this much puts it at the bowl.
 */
export function railRollFrom(i: number): { x: number; y: number } {
  const [sx, sy] = RAIL.slots[i]!;
  const [mx, my] = RAIL.mouth;
  const s = RAIL.scale;
  return { x: ((mx - sx) / (BALL[2] * s)) * 100, y: ((my - sy) / (BALL[3] * s)) * 100 };
}
